"use client";

import { ChevronLeft } from "@mui/icons-material";
import { Box, BoxProps } from "@mui/material";
import { BigNumber } from "bignumber.js";
import { BigNumberish } from "ethers";
import { compactInteger } from "modules/shared/utils/compactInteger";
import { forwardRef, InputHTMLAttributes } from "react";
import { NumericFormat, NumericFormatProps } from "react-number-format";

export type FormattedNumberProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value"
> &
  Omit<NumericFormatProps<unknown>, "value"> & {
    value: NumericFormatProps<unknown>["value"] | BigNumber | BigNumberish;
    humanize?: boolean;
    gap?: string | number;
    min?: number;
    wrapperProps?: BoxProps;
    customInputProps?: any;
  };

export const FormattedNumber = forwardRef<
  HTMLInputElement,
  FormattedNumberProps
>(
  (
    {
      displayType = "text",
      prefix,
      suffix,
      decimalScale = 2,
      value,
      humanize,
      gap = "0.315em",
      min = 0.01,
      wrapperProps,
      customInputProps,
      ...props
    },
    forwardedRef
  ) => {
    let valueToFormat = value;
    let isLessThanMinimum = false;

    if (BigNumber.isBigNumber(value)) {
      isLessThanMinimum = value.isGreaterThan(0) && value.isLessThan(min);
      valueToFormat = parseFloat(
        value.toFixed(decimalScale, BigNumber.ROUND_HALF_UP)
      );
    } else {
      const parsedValue = parseFloat(value?.toString() || "0");

      isLessThanMinimum = parsedValue < min && parsedValue > 0;
    }

    return (
      <Box display="inline" component="span" {...wrapperProps}>
        {prefix ? prefix + " " : false}
        {humanize ? (
          <span>${compactInteger(value, decimalScale)}</span>
        ) : isLessThanMinimum && displayType === "text" ? (
          <span>
            <ChevronLeft
              sx={{
                fontSize: "inherit",
                height: "0.7em",
                marginRight: "-.1em",
                marginLeft: "-0.1em",
                transform: "scale(2)",
                display: "inline-block",
              }}
            />
            {min}
          </span>
        ) : (
          <NumericFormat
            getInputRef={forwardedRef}
            decimalScale={decimalScale}
            inputMode="decimal"
            displayType={displayType}
            thousandSeparator=" "
            allowLeadingZeros={false}
            {...props}
            {...customInputProps}
            value={valueToFormat as NumericFormatProps<unknown>["value"]}
          />
        )}

        {suffix ? " " + suffix : false}
      </Box>
    );
  }
);

FormattedNumber.displayName = "FormattedNumber";
